import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, errMsg } from '../lib/api';
import { useDocumentTitle, useTid } from '../lib/hooks';
import { CLASSE_COR, classificar, fmtDayMon, GRAVIDADE_LABEL, TIPO_LABEL, plural } from '../lib/format';
import type { NcTipo, NotaResumo, PhotoItem, Restaurant, TenantInfo, Tracking, VisitSummary } from '../lib/types';
import { Avatar, Button, ErrorState, Field, GravPill, Loading, NotaBadge, PhotoTile, Sheet, VisitRow, useConfirm } from '../components/ui';
import { NcSheet } from '../components/NcSheet';
import { RestaurantForm } from '../components/RestaurantForm';
import { ShareSheet } from '../components/ShareSheet';
import { useStartVisit } from '../components/StartVisit';
import { photoToLightbox, useLightbox, useToast } from '../components/feedback';

const TABS: Array<[string, string]> = [['visitas', 'Visitas'], ['acompanhamento', 'Acompanhamento'], ['fotos', 'Fotos'], ['acesso', 'Acesso']];

export function RestaurantDetail() {
  const tid = useTid();
  const { rid, tab = 'visitas' } = useParams();
  const info = useOutletContext<TenantInfo>();
  const nav = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const [editing, setEditing] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteName, setDeleteName] = useState('');
  const { start, busy } = useStartVisit(tid);

  const q = useQuery({
    queryKey: ['restaurant', tid, rid],
    queryFn: () => api.get<{ restaurant: Restaurant; visits: VisitSummary[]; notas: NotaResumo | null }>(`/api/t/${tid}/restaurants/${rid}`),
  });
  useDocumentTitle(q.data?.restaurant.nome);

  const archive = useMutation({
    mutationFn: (archived: boolean) => api.post(`/api/t/${tid}/restaurants/${rid}/archive`, { archived }),
    onSuccess: (_d, archived) => {
      qc.invalidateQueries({ queryKey: ['restaurants', tid] });
      qc.invalidateQueries({ queryKey: ['restaurant', tid, rid] });
      toast(archived ? 'Restaurante arquivado' : 'Restaurante reativado');
      if (archived) nav(`/t/${tid}/restaurantes`);
    },
    onError: (e) => toast(errMsg(e), 'err'),
  });

  const remove = useMutation({
    mutationFn: () => api.del(`/api/t/${tid}/restaurants/${rid}`, { confirmNome: deleteName }),
    onSuccess: () => {
      for (const k of ['home', 'restaurants', 'visits', 'period']) qc.invalidateQueries({ queryKey: [k, tid] });
      toast('Restaurante excluído');
      nav(`/t/${tid}/restaurantes`, { replace: true });
    },
    onError: (e) => toast(errMsg(e), 'err'),
  });

  if (q.error) return <ErrorState message={errMsg(q.error)} onRetry={() => q.refetch()} />;
  if (!q.data) return <Loading rows={3} height={110} />;
  const r = q.data.restaurant;
  const inProgress = q.data.visits.find((v) => v.status === 'em_andamento');
  const finalizadas = q.data.visits.filter((v) => v.status === 'finalizada'); // mais recente primeiro

  return (
    <div>
      <Link to={`/t/${tid}/restaurantes`} className="link-btn muted" style={{ fontSize: 13, display: 'inline-block', marginBottom: 12 }}>← Restaurantes</Link>
      <section className="card" style={{ padding: 17, marginBottom: 14 }}>
        <div className="row wrap gap-12" style={{ alignItems: 'flex-start' }}>
          <div className="flex1" style={{ minWidth: 200 }}>
            <h1 style={{ fontSize: 19, fontWeight: 600, letterSpacing: '-.02em', margin: '0 0 4px' }}>{r.nome}</h1>
            <div className="mono" style={{ fontSize: 12, color: 'var(--text-2)' }}>{r.cnpj ?? 'CNPJ não informado'} · {TIPO_LABEL[r.tipo]}</div>
          </div>
          <NotaBadge
            nota={q.data.notas?.media}
            size="lg"
            label="Nota média"
            extra={
              <>
                {q.data.notas?.ultima != null && q.data.notas.visitasComNota > 0 && <>última visita: {q.data.notas.ultima}<br /></>}
                {q.data.notas?.inicial != null && <>inclui nota anterior ({q.data.notas.inicial})</>}
              </>
            }
          />
          <div className="row wrap gap-8" style={{ flexBasis: '100%' }}>
            {info.can.editRestaurant && <Button size="sm" onClick={() => setEditing(true)}>Editar</Button>}
            {info.can.share && <Button size="sm" onClick={() => setSharing(true)}>Compartilhar</Button>}
            {info.can.writeVisits && !r.archivedAt && (
              <Button variant="dark" size="sm" loading={busy === r.id} onClick={() => start(r.id)}>{inProgress ? 'Continuar visita' : 'Iniciar visita'}</Button>
            )}
          </div>
        </div>
        <div style={{ marginTop: 14, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
          <div>
            <div className="k">Contato responsável</div>
            <div style={{ fontSize: 13, fontWeight: 500 }}>{r.contatoNome ?? '—'}</div>
            {r.contatoTelefone && <a className="mono" href={`tel:${r.contatoTelefone.replace(/\D/g, '')}`} style={{ fontSize: 11.5, color: 'var(--text-2)', display: 'block', marginTop: 2 }}>{r.contatoTelefone}</a>}
            {r.contatoEmail && <a href={`mailto:${r.contatoEmail}`} style={{ fontSize: 11.5, display: 'block', marginTop: 2 }}>{r.contatoEmail}</a>}
          </div>
          <div>
            <div className="k">Nutricionista responsável</div>
            <div style={{ fontSize: 13, fontWeight: 500 }}>{r.nutri?.nome ?? '—'}</div>
            {r.nutri?.crn && <div className="mono" style={{ fontSize: 11.5, color: 'var(--text-2)', marginTop: 2 }}>{r.nutri.crn}</div>}
          </div>
          {r.razaoSocial && (
            <div>
              <div className="k">Razão social</div>
              <div style={{ fontSize: 13, fontWeight: 500 }}>{r.razaoSocial}</div>
            </div>
          )}
        </div>
        {(finalizadas.some((v) => v.nota !== null && v.nota !== undefined) || r.notaInicial !== null) && (
          <NotaEvolucao visits={finalizadas} tid={tid} inicial={r.notaInicial} />
        )}
        {r.archivedAt && (
          <div className="row between wrap gap-8" style={{ marginTop: 14, padding: '10px 12px', borderRadius: 10, background: 'var(--persist-soft)', color: 'var(--persist-text)', fontSize: 13 }}>
            Restaurante arquivado: não aparece nas listas e não recebe visitas.
            {info.can.editRestaurant && <Button size="xs" loading={archive.isPending} onClick={() => archive.mutate(false)}>Reativar</Button>}
          </div>
        )}
      </section>

      <div className="chips" role="tablist">
        {TABS.map(([k, l]) => (
          <Link key={k} to={`/t/${tid}/restaurantes/${rid}/${k}`} replace className={`chip ${tab === k ? 'on' : ''}`} role="tab" aria-selected={tab === k} style={{ display: 'inline-flex', alignItems: 'center' }}>{l}</Link>
        ))}
      </div>

      {tab === 'visitas' && (
        <div className="stack gap-8">
          {q.data.visits.map((v) =>
            v.status === 'em_andamento' ? (
              <VisitRow key={v.id} v={v} to={`/t/${tid}/visita/${v.id}`} title={`Visita em andamento · ${v.authorNome ?? ''}`} showRest={false} cta="Continuar →" />
            ) : (
              <VisitRow key={v.id} v={v} to={`/t/${tid}/relatorio/${v.id}`} showRest={false} cta="Relatório →" />
            ),
          )}
          {q.data.visits.length === 0 && <div className="empty">Nenhuma visita registrada ainda.</div>}
        </div>
      )}
      {tab === 'acompanhamento' && <TrackingTab tid={tid} rid={rid!} />}
      {tab === 'fotos' && <PhotosTab tid={tid} rid={rid!} canDelete={info.can.writeVisits} />}
      {tab === 'acesso' && <AccessTab tid={tid} rid={rid!} canShare={info.can.share} onManage={() => setSharing(true)} />}

      {info.can.editRestaurant && (
        <div className="row wrap gap-16" style={{ marginTop: 28, justifyContent: 'center' }}>
          {!r.archivedAt && (
            <button
              className="link-btn muted u"
              onClick={async () => {
                if (await confirm.ask({ title: 'Arquivar restaurante?', message: 'Ele some das listas e não recebe novas visitas. O histórico e os relatórios continuam guardados.', cta: 'Arquivar' })) archive.mutate(true);
              }}
            >
              Arquivar restaurante
            </button>
          )}
          <button className="link-btn u" style={{ color: 'var(--nc-text)' }} onClick={() => { setDeleteName(''); setDeleting(true); }}>Excluir restaurante</button>
        </div>
      )}

      <Sheet open={deleting} onClose={() => setDeleting(false)} title="Excluir restaurante">
        <div className="sub" style={{ marginBottom: 12 }}>
          Apaga <strong style={{ color: 'var(--text)' }}>{q.data.visits.length} {q.data.visits.length === 1 ? 'visita' : 'visitas'}</strong>, todas as fotos, pendências, relatórios em PDF e compartilhamentos desta unidade. <strong style={{ color: 'var(--nc-text)' }}>Não dá para desfazer.</strong>
          <br />Se só quer tirar da lista, use "Arquivar".
        </div>
        <Field label={`Para confirmar, digite: ${r.nome}`}>
          <input className="input" value={deleteName} onChange={(e) => setDeleteName(e.target.value)} placeholder={r.nome} />
        </Field>
        <Button
          variant="danger-solid" size="lg" block loading={remove.isPending}
          disabled={deleteName.trim().toLowerCase() !== r.nome.trim().toLowerCase()}
          onClick={() => remove.mutate()}
        >
          Excluir definitivamente
        </Button>
      </Sheet>

      <RestaurantForm tid={tid} open={editing} onClose={() => setEditing(false)} initial={r} />
      <ShareSheet tid={tid} target={sharing ? { type: 'restaurant', id: r.id } : null} onClose={() => setSharing(false)} />
      {confirm.node}
    </div>
  );
}

const CELL: Record<NcTipo, { mark: string; bg: string; fg: string }> = {
  aberta: { mark: '●', bg: '#FBEFEC', fg: '#B05B41' },
  persiste: { mark: '●', bg: '#FAF2E6', fg: '#A87A2E' },
  resolvida: { mark: '✓', bg: '#EDF2EF', fg: '#3F5A4C' },
};

function TrackingTab({ tid, rid }: { tid: string; rid: string }) {
  const q = useQuery({ queryKey: ['tracking', tid, rid], queryFn: () => api.get<Tracking>(`/api/t/${tid}/restaurants/${rid}/tracking`) });
  const [ncOpen, setNcOpen] = useState<string | null>(null);
  if (!q.data) return <Loading rows={3} />;
  const t = q.data;
  return (
    <>
      <NcSheet base={`/api/t/${tid}/ncs`} ncId={ncOpen} onClose={() => setNcOpen(null)} reportHref={(vid) => `/t/${tid}/relatorio/${vid}`} />
      <section className="card" style={{ marginBottom: 14 }}>
        <h2 className="h2" style={{ marginBottom: 3 }}>Evolução das pendências</h2>
        <div style={{ fontSize: 12.5, color: 'var(--text-2)', marginBottom: 14 }}>Cada coluna é uma visita. Leia da esquerda para a direita.</div>
        {t.rows.length === 0 ? (
          <div className="empty plain">Nenhuma pendência registrada nesta unidade.</div>
        ) : (
          <div className="matrix">
            <div className="matrix-in">
              <div className="mrow head">
                <div className="mt" style={{ fontSize: 11, color: 'var(--text-2)' }}>Pendência</div>
                {t.cols.map((c) => (
                  <Link key={c.visitId} to={`/t/${tid}/relatorio/${c.visitId}`} className="mcell mono" style={{ fontSize: 10.5, color: 'var(--text-2)', fontWeight: 400 }} title={`Visita nº ${c.numero}`}>{fmtDayMon(c.date)}</Link>
                ))}
              </div>
              <div className="mrow">
                <div className="mt" style={{ fontSize: 11.5, fontWeight: 600 }}>Nota da visita</div>
                {t.cols.map((c) => {
                  const cor = c.nota !== null ? CLASSE_COR[classificar(c.nota)] : null;
                  return <div key={c.visitId} className="mcell mono" style={{ background: cor?.bg ?? '#F7F8F8', color: cor?.fg ?? 'var(--faint)', fontWeight: 500 }}>{c.nota ?? '—'}</div>;
                })}
              </div>
              {t.rows.map((row) => (
                <div key={row.ncId} className="mrow" onClick={() => setNcOpen(row.ncId)} style={{ cursor: 'pointer' }} role="button" aria-label={`Ver fotos: ${row.titulo}`}>
                  <div className="mt">
                    <div style={{ fontSize: 12.5, fontWeight: 500, lineHeight: 1.3 }}>{row.titulo}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-2)', marginTop: 1 }}>{row.setor} · {GRAVIDADE_LABEL[row.gravidade]}</div>
                  </div>
                  {row.cells.map((c, i) => {
                    const s = c ? CELL[c] : { mark: '', bg: '#F7F8F8', fg: '#C6CFCB' };
                    return <div key={i} className="mcell" style={{ background: s.bg, color: s.fg }} aria-label={c ?? 'sem registro'}>{s.mark}</div>;
                  })}
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="legend">
          <span><span className="sw" style={{ background: '#FBEFEC', color: '#B05B41' }}>●</span>Nova</span>
          <span><span className="sw" style={{ background: '#FAF2E6', color: '#A87A2E' }}>●</span>Persiste</span>
          <span><span className="sw" style={{ background: '#EDF2EF', color: '#3F5A4C' }}>✓</span>Resolvida</span>
        </div>
      </section>
      {t.open.length > 0 && <div className="section-head"><h2 className="h2">Abertas</h2><span className="counter-mono">{t.open.length}</span></div>}
      <div className="stack gap-8">
        {t.open.map((n) => {
          const old = n.ageVisits > 1;
          return (
            <button key={n.ncId} className="card item" style={{ display: 'block', padding: 13, borderRadius: 12 }} onClick={() => setNcOpen(n.ncId)}>
              <div className="row gap-10" style={{ alignItems: 'flex-start' }}>
                <div className="flex1">
                  <div style={{ fontSize: 13.5, fontWeight: 500, lineHeight: 1.35 }}>{n.titulo}</div>
                  <div className="small wrapline" style={{ marginTop: 3 }}>{n.setor} · aberta em {fmtDayMon(n.openedAt)} · {plural(n.ageVisits, 'visita', 'visitas')}</div>
                </div>
                <div className="none stack gap-4" style={{ alignItems: 'flex-end' }}>
                  <span className={`pill upper ${old ? 'per' : 'new'}`}>{old ? 'persiste' : 'nova'}</span>
                  <GravPill g={n.gravidade} />
                </div>
              </div>
              {n.reply && (
                <div className="reply" style={{ marginTop: 9, fontSize: 12.5 }}><span style={{ fontWeight: 500 }}>Resposta do cliente:</span> {n.reply.texto}</div>
              )}
              <div className="small" style={{ marginTop: 8, color: 'var(--accent)' }}>Ver fotos e histórico ›</div>
            </button>
          );
        })}
      </div>
    </>
  );
}

/** Galeria com scroll infinito: carrega 30 por vez, sem travar com centenas de fotos. */
function PhotosTab({ tid, rid, canDelete }: { tid: string; rid: string; canDelete: boolean }) {
  const lightbox = useLightbox();
  const qc = useQueryClient();
  const toast = useToast();
  const [filter, setFilter] = useState('');
  const [setores, setSetores] = useState<string[]>([]);
  type Page = { items: Array<PhotoItem & { visitId: string; visitNumero: number | null }>; nextCursor: string | null; setores?: string[] };
  const q = useInfiniteQuery({
    queryKey: ['photos', tid, rid, filter],
    initialPageParam: '',
    queryFn: async ({ pageParam }) => {
      const p = new URLSearchParams({ limit: '30' });
      if (filter) p.set('setor', filter);
      if (pageParam) p.set('cursor', pageParam);
      const page = await api.get<Page>(`/api/t/${tid}/restaurants/${rid}/photos?${p}`);
      if (page.setores && !filter) setSetores(page.setores);
      return page;
    },
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const sentinel = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = sentinel.current;
    if (!el) return;
    const io = new IntersectionObserver((e) => { if (e[0].isIntersecting && q.hasNextPage && !q.isFetchingNextPage) void q.fetchNextPage(); }, { rootMargin: '600px' });
    io.observe(el);
    return () => io.disconnect();
  }, [q.hasNextPage, q.isFetchingNextPage, q.fetchNextPage]); // eslint-disable-line react-hooks/exhaustive-deps

  const items = q.data?.pages.flatMap((p) => p.items) ?? [];
  const open = (p: Page['items'][number]) =>
    lightbox({
      ...photoToLightbox(p),
      onDelete: canDelete
        ? async () => {
            await api.del(`/api/t/${tid}/visits/${p.visitId}/photos/${p.id}`);
            await qc.invalidateQueries({ queryKey: ['photos', tid, rid] });
            qc.invalidateQueries({ queryKey: ['report', tid, p.visitId] });
            toast('Foto excluída');
          }
        : undefined,
    });

  return (
    <>
      {setores.length > 1 && (
        <div className="chips" style={{ paddingBottom: 10 }}>
          {['', ...setores].map((s) => (
            <button key={s || 'todos'} className={`chip sm ${filter === s ? 'on' : ''}`} onClick={() => setFilter(s)} aria-pressed={filter === s}>{s || 'Todos'}</button>
          ))}
        </div>
      )}
      {q.isLoading && <Loading rows={2} height={160} />}
      <div className="grid-photos">
        {items.map((p) => <PhotoTile key={p.id} p={p} stamp={`Visita nº ${p.visitNumero ?? '—'} · ${fmtDayMon(p.visitDate ?? p.capturedAt)}`} onOpen={() => open(p)} />)}
      </div>
      <div ref={sentinel} style={{ height: 1 }} />
      {q.isFetchingNextPage && <div style={{ textAlign: 'center', padding: 16 }}><span className="spinner" style={{ color: 'var(--accent)' }} /></div>}
      {q.data && !items.length && <div className="empty">Nenhuma foto registrada ainda.</div>}
      {q.data && items.length > 0 && !q.hasNextPage && <div className="small" style={{ textAlign: 'center', padding: 14 }}>{items.length} {items.length === 1 ? 'foto' : 'fotos'}</div>}
    </>
  );
}

/** Mini-gráfico da nota visita a visita (mais antiga → mais recente). */
function NotaEvolucao({ visits, tid, inicial }: { visits: VisitSummary[]; tid: string; inicial: number | null }) {
  const list = visits.filter((v) => v.nota !== null && v.nota !== undefined).slice(0, 12).reverse();
  return (
    <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--divider)' }}>
      <div className="k" style={{ marginBottom: 8 }}>Evolução da nota{list.length ? ` · últimas ${list.length} visitas` : ''}</div>
      <div className="row gap-6" style={{ alignItems: 'flex-end', height: 74, overflowX: 'auto' }}>
        {inicial !== null && (
          <div className="stack none" style={{ alignItems: 'center', gap: 3, width: 50 }} title="Nota trazida do sistema anterior">
            <span className="mono" style={{ fontSize: 10, color: CLASSE_COR[classificar(inicial)].fg }}>{inicial}</span>
            <span style={{ width: 22, height: Math.max(6, (inicial / 100) * 42), borderRadius: 5, background: 'repeating-linear-gradient(135deg,#C6CFCB 0 4px,#E3E7E6 4px 8px)' }} />
            <span className="mono" style={{ fontSize: 9.5, color: 'var(--text-2)' }}>anterior</span>
          </div>
        )}
        {list.map((v) => {
          const c = CLASSE_COR[classificar(v.nota!)];
          return (
            <Link key={v.id} to={`/t/${tid}/relatorio/${v.id}`} className="stack none" style={{ alignItems: 'center', gap: 3, width: 34, color: 'inherit' }} title={`Visita nº ${v.numero}: ${v.nota}`}>
              <span className="mono" style={{ fontSize: 10, color: c.fg }}>{v.nota}</span>
              <span style={{ width: 22, height: Math.max(6, (v.nota! / 100) * 42), borderRadius: 5, background: c.fg, opacity: 0.85 }} />
              <span className="mono" style={{ fontSize: 9.5, color: 'var(--text-2)' }}>{fmtDayMon(v.finishedAt ?? v.startedAt).slice(0, 5)}</span>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

function AccessTab({ tid, rid, canShare, onManage }: { tid: string; rid: string; canShare: boolean; onManage: () => void }) {
  const q = useQuery({
    queryKey: ['access', tid, rid],
    queryFn: () => api.get<{ team: Array<{ userId: string; nome: string; email: string; src: string; perm: string }>; shared: Array<{ id: string; nome: string; email: string; permLabel: string; isCompany: boolean; pending: boolean }> }>(`/api/t/${tid}/restaurants/${rid}/access`),
  });
  return (
    <section className="card">
      <div className="row wrap gap-12" style={{ alignItems: 'flex-start', marginBottom: 14 }}>
        <div className="flex1" style={{ minWidth: 200 }}>
          <h2 className="h2" style={{ marginBottom: 3 }}>Quem acessa este restaurante</h2>
          <div style={{ fontSize: 12.5, color: 'var(--text-2)', lineHeight: 1.5 }}>Relatórios e fotos herdam estas permissões. Cada relatório pode ser compartilhado à parte.</div>
        </div>
        {canShare && <Button variant="accent" size="sm" onClick={onManage}>Gerenciar acesso</Button>}
      </div>
      {!q.data ? (
        <Loading rows={3} height={44} />
      ) : (
        <div className="stack">
          {q.data.team.map((p) => (
            <div key={p.userId} className="row gap-10" style={{ padding: '10px 0', borderTop: '1px solid var(--divider-2)', gap: 11 }}>
              <Avatar name={p.nome} />
              <div className="flex1">
                <div className="wrapline" style={{ fontSize: 13.5, fontWeight: 500 }}>{p.nome}</div>
                <div style={{ fontSize: 11.5, color: 'var(--text-2)' }}>{p.src}</div>
              </div>
              <span className="mono none" style={{ fontSize: 11, color: 'var(--text-3)' }}>{p.perm}</span>
            </div>
          ))}
          {q.data.shared.map((p) => (
            <div key={p.id} className="row gap-10" style={{ padding: '10px 0', borderTop: '1px solid var(--divider-2)', gap: 11 }}>
              <Avatar name={p.nome} color="#5F7386" />
              <div className="flex1">
                <div className="wrapline" style={{ fontSize: 13.5, fontWeight: 500 }}>{p.nome}</div>
                <div className="wrapline" style={{ fontSize: 11.5, color: 'var(--text-2)' }}>{p.isCompany ? 'Empresa' : 'Nutricionista'} · {p.email}{p.pending ? ' · pendente' : ''}</div>
              </div>
              <span className="mono none" style={{ fontSize: 11, color: 'var(--text-3)' }}>{p.permLabel}</span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
